/**
 * Utilitaires pour la facturation et les calculs de montants facturables
 */

/**
 * Calcule le montant à facturer pour un projet basé sur les jours consommés et un taux
 * @param {number} jours - Nombre de jours consommés
 * @param {number} taux - Taux journalier en euros
 * @returns {number} Montant à facturer en euros
 */
export const calculateBillingAmount = (jours, taux = 350) => {
  return parseFloat(jours) * parseFloat(taux);
};

/**
 * Formate un montant en euros avec les paramètres régionaux français
 * @param {number} amount - Montant à formater
 * @returns {string} Montant formaté
 */
export const formatCurrency = (amount) => {
  return parseFloat(amount).toLocaleString('fr-FR', {
    style: 'currency',
    currency: 'EUR'
  });
};

/**
 * Calcule les statistiques de facturation pour une liste de projets
 * @param {Array} projects - Liste des projets avec jours consommés
 * @returns {Object} Objet avec totalAmount, totalDays, projectCount
 */
export const calculateBillingStats = (projects) => {
  return {
    totalAmount: projects.reduce((sum, p) => sum + (p.montant_a_facturer || 0), 0),
    totalDays: projects.reduce((sum, p) => sum + (p.jours_consommes || 0), 0),
    projectCount: projects.length,
    averageAmount: projects.length > 0 
      ? projects.reduce((sum, p) => sum + (p.montant_a_facturer || 0), 0) / projects.length
      : 0
  };
};

/**
 * Récupère le statut de facturation d'un projet
 * @param {Object} project - Objet projet
 * @returns {string} Statut: 'facturé', 'partiellement_facturé' ou 'non_facturé'
 */
export const getBillingStatus = (project) => {
  if (!project.montant_a_facturer || project.montant_a_facturer === 0) {
    return 'non_facturé';
  }
  
  if (project.montant_facture === project.montant_a_facturer) {
    return 'facturé';
  }
  
  if (project.montant_facture && project.montant_facture > 0) {
    return 'partiellement_facturé';
  }
  
  return 'non_facturé';
};

/**
 * Génère un résumé de facturation pour un projet
 * @param {Object} project - Objet projet
 * @returns {Object} Objet avec résumé de facturation
 */
export const getBillingSummary = (project) => {
  const status = getBillingStatus(project);
  
  return {
    nomProjet: project.nom,
    dateFinProvisionnelle: project.date_fin,
    jourConsommes: parseFloat(project.jours_consommes || 0),
    montantAFacturer: parseFloat(project.montant_a_facturer || 0),
    montantFacture: parseFloat(project.montant_facture || 0),
    montantRestant: parseFloat(project.montant_a_facturer || 0) - parseFloat(project.montant_facture || 0),
    statut: status,
    taux: project.taux || 350,
    categorie: project.categorie
  };
};

/**
 * Filtre les projets facturables
 * @param {Array} projects - Liste des projets
 * @returns {Array} Projets facturables
 */
export const getFacturableProjects = (projects) => {
  return (projects || []).filter(p => p.facturable === true || p.facturable === 1);
};

/**
 * Filtre les projets par statut de facturation
 * @param {Array} projects - Liste des projets
 * @param {string} status - Statut à filtrer ('facturé', 'partiellement_facturé', 'non_facturé')
 * @returns {Array} Projets filtrés
 */
export const filterProjectsByBillingStatus = (projects, status) => {
  return projects.filter(p => getBillingStatus(p) === status);
};

/**
 * Trie les projets par montant à facturer décroissant
 * @param {Array} projects - Liste des projets
 * @returns {Array} Projets triés
 */
export const sortByBillingAmount = (projects) => {
  return [...projects].sort((a, b) => (b.montant_a_facturer || 0) - (a.montant_a_facturer || 0));
};

/**
 * Trie les projets par date de facturation prévue
 * @param {Array} projects - Liste des projets
 * @returns {Array} Projets triés
 */
export const sortByBillingDate = (projects) => {
  return [...projects].sort((a, b) => {
    const dateA = new Date(a.date_fin || '9999-12-31');
    const dateB = new Date(b.date_fin || '9999-12-31');
    return dateA - dateB;
  });
};


